import { useEffect, useRef, useState } from 'react'
import { AgeGroup } from 'constants/components'

type DacSkipButtonConfig = {
  isDacPage: boolean
  isDacCtaFlowEnabled: boolean
  isDacShowOnCtaClick: boolean
  isPremium: boolean
  isYoungOrMiddleAgeGroup: boolean
  ageGroup: AgeGroup
  dacWidgetState: string
  /**
   * Text to show on the skip button when isDacCtaFlowEnabled is false
   * (e.g. for legacy flows that still render a skip button independently).
   */
  fallbackButtonText?: string
  /**
   * Custom timeout calculator. Receives isPremium and ageGroup, returns ms.
   * Defaults to: premium=15s, young=60s, others=20s.
   */
  getTimeout?: (isPremium: boolean, ageGroup: AgeGroup) => number
  /**
   * Custom callback on timer fire. If provided, runs instead of the default
   * "set reason to 'timer' and show button" logic. Useful when certain age
   * groups should show the button without tracking a reason (e.g. FFG middle-age).
   */
  onTimerFire?: (params: {
    setShowDacSkipButton: (value: boolean) => void
    setSkipButtonShownReason: (reason: string) => void
    isDacCtaFlowEnabled: boolean
  }) => void
}

type DacSkipButtonResult = {
  showDacSkipButton: boolean
  skipButtonText: string
  skipButtonShownReason: string
}

const defaultGetTimeout = (isPremium: boolean, ageGroup: AgeGroup): number => {
  if (isPremium) return 15000
  if (ageGroup === AgeGroup.YOUNG_AGE_GROUP) return 60000
  return 20000
}

export const useDacSkipButton = ({
  isDacPage,
  isDacCtaFlowEnabled,
  isDacShowOnCtaClick,
  isPremium,
  isYoungOrMiddleAgeGroup,
  ageGroup,
  dacWidgetState,
  fallbackButtonText,
  getTimeout = defaultGetTimeout,
  onTimerFire
}: DacSkipButtonConfig): DacSkipButtonResult => {
  const [showDacSkipButton, setShowDacSkipButton] = useState(false)
  const [skipButtonText, setSkipButtonText] = useState('')
  const [skipButtonShownReason, setSkipButtonShownReason] = useState('')

  const isDacCtaFlowEnabledRef = useRef(isDacCtaFlowEnabled)
  const showDacSkipButtonRef = useRef(showDacSkipButton)

  useEffect(() => {
    isDacCtaFlowEnabledRef.current = isDacCtaFlowEnabled
  }, [isDacCtaFlowEnabled])

  useEffect(() => {
    showDacSkipButtonRef.current = showDacSkipButton
  }, [showDacSkipButton])

  // Show skip button when DAC CTA is clicked
  useEffect(() => {
    if (!isDacShowOnCtaClick) return
    setShowDacSkipButton(true)
    setSkipButtonShownReason('dac_widget_loaded_with_show_on_cta_click')
  }, [isDacShowOnCtaClick])

  // Determine skip button text
  useEffect(() => {
    if (isDacCtaFlowEnabled) {
      setSkipButtonText(
        isPremium
          ? 'Weiter ohne digitalen Kontoauszug'
          : 'Ich habe keinen Online-Banking-Zugang'
      )
    } else if (fallbackButtonText) {
      setSkipButtonText(fallbackButtonText)
    }
  }, [isDacCtaFlowEnabled, isPremium, fallbackButtonText])

  // Timer-based skip button reveal
  useEffect(() => {
    if (!isDacPage || !isDacCtaFlowEnabled) return

    const timeout = getTimeout(isPremium, ageGroup)

    const timeoutId = window.setTimeout(() => {
      if (showDacSkipButtonRef.current) return

      if (onTimerFire) {
        onTimerFire({
          setShowDacSkipButton,
          setSkipButtonShownReason,
          isDacCtaFlowEnabled: isDacCtaFlowEnabledRef.current
        })
      } else {
        setSkipButtonShownReason('timer')
        setShowDacSkipButton(isDacCtaFlowEnabledRef.current)
      }
    }, timeout)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    isDacPage,
    isDacCtaFlowEnabled,
    isPremium,
    ageGroup,
    getTimeout,
    onTimerFire
  ])

  // Show skip button when user leaves the tab
  useEffect(() => {
    if (
      !isDacCtaFlowEnabled ||
      !isDacPage ||
      !isYoungOrMiddleAgeGroup ||
      isPremium
    ) {
      return
    }

    const onTabVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return

      setSkipButtonShownReason('left_tab')
      setShowDacSkipButton(true)
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }

    document.addEventListener('visibilitychange', onTabVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onTabVisibilityChange)
    }
  }, [isDacCtaFlowEnabled, isDacPage, isYoungOrMiddleAgeGroup, isPremium])

  // Show skip button on DAC widget error
  useEffect(() => {
    if (dacWidgetState === 'error' && !isPremium) {
      setShowDacSkipButton(true)
      setSkipButtonShownReason('error_in_dac')
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }
  }, [dacWidgetState, isPremium])

  return { showDacSkipButton, skipButtonText, skipButtonShownReason }
}
